import React, { useState, useMemo } from 'react';
import { TtOrderEvent, useListTTOrders, OrderEventType } from '@/lib/api/risk/brokerage/tt';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn, CommonDestinationTimezone } from '@/lib/utils';
import { DataTable } from '@/components/ui/DataTable/index';
import { DataTableColumn } from '@/components/ui/DataTable/types';
import moment from 'moment-timezone';

const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    
    return {
        startDate: formatDate(sevenDaysAgo),
        endDate: formatDate(today),
    };
};

export function TTOrdersTab() {
    const defaultDates = getDefaultDates();
    const [startDate, setStartDate] = useState<string>(defaultDates.startDate);
    const [endDate, setEndDate] = useState<string>(defaultDates.endDate);
    const [selectedEventTypes, setSelectedEventTypes] = useState<OrderEventType[]>([OrderEventType.Filled]);

    const allEventTypes = Object.values(OrderEventType);

    const toggleEventType = (eventType: OrderEventType) => {
        setSelectedEventTypes((prev) => {
            if (prev.includes(eventType)) {
                return prev.filter((type) => type !== eventType);
            } else {
                return [...prev, eventType];
            }
        });
    };

    const queryParams = useMemo(() => {
        const params: { start_date?: string; end_date?: string; event_types?: OrderEventType[] } = {};
        
        if (startDate) {
            params.start_date = startDate;
        }
        if (endDate) {
            params.end_date = endDate;
        }
        if (selectedEventTypes.length > 0) {
            params.event_types = selectedEventTypes;
        }
        
        return params;
    }, [startDate, endDate, selectedEventTypes]);

    const shouldEnableQuery = !!startDate && !!endDate;

    const ttOrdersResult = useListTTOrders(queryParams, { enabled: shouldEnableQuery });
    const events: TtOrderEvent[] = ttOrdersResult.data?.data?.events ?? [];

    const columns: DataTableColumn<TtOrderEvent>[] = useMemo(() => [
        {
            id: "event_type",
            accessorKey: "event_type",
            header: "Event Type",
            size: 120,
            minSize: 100,
        },
        {
            id: "event_time",
            accessorKey: "event_time",
            header: "Event Time",
            size: 180,
            minSize: 150,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value ? moment.tz(value, CommonDestinationTimezone).format("YYYY-MM-DD HH:mm:ss") : "";
            },
        },
        {
            id: "instrument_name",
            accessorKey: "instrument_name",
            header: "Instrument",
            size: 200,
            minSize: 150,
            enableTooltip: true,
        },
        {
            id: "instrument_product",
            accessorKey: "instrument_product",
            header: "Product",
            size: 120,
            minSize: 100,
        },
        {
            id: "order_type",
            accessorKey: "order_type",
            header: "Order Type",
            size: 120,
            minSize: 100,
        },
        {
            id: "buy_sell_type",
            accessorKey: "buy_sell_type",
            header: "Buy/Sell",
            size: 100,
            minSize: 80,
        },
        {
            id: "order_quantity",
            accessorKey: "order_quantity",
            header: "Order Qty",
            size: 120,
            minSize: 100,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value || "";
            },
        },
        {
            id: "filled_quantity",
            accessorKey: "filled_quantity",
            header: "Filled Qty",
            size: 120,
            minSize: 100,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value || "";
            },
        },
        {
            id: "working_quantity",
            accessorKey: "working_quantity",
            header: "Working Qty",
            size: 120,
            minSize: 100,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value || "";
            },
        },
        {
            id: "limit_price",
            accessorKey: "limit_price",
            header: "Limit Price",
            size: 120,
            minSize: 100,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value || "";
            },
        },
        {
            id: "avg_fill_price",
            accessorKey: "avg_fill_price",
            header: "Avg Fill Price",
            size: 130,
            minSize: 110,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value || "";
            },
        },
        {
            id: "text_a",
            accessorKey: "text_a",
            header: "Text A",
            size: 150,
            minSize: 100,
        },
        {
            id: "text_b",
            accessorKey: "text_b",
            header: "Text B",
            size: 150,
            minSize: 100,
        },
        {
            id: "text_c",
            accessorKey: "text_c",
            header: "Text C",
            size: 120,
            minSize: 100,
        },
        {
            id: "text_tt",
            accessorKey: "text_tt",
            header: "Text TT",
            size: 120,
            minSize: 100,
        },
        {
            id: "time_in_force",
            accessorKey: "time_in_force",
            header: "Time In Force",
            size: 130,
            minSize: 110,
        },
        {
            id: "trading_account_name",
            accessorKey: "trading_account_name",
            header: "Trading Account",
            size: 180,
            minSize: 150,
            enableTooltip: true,
        },
        {
            id: "clearing_account_name",
            accessorKey: "clearing_account_name",
            header: "Clearing Account",
            size: 180,
            minSize: 150,
            enableTooltip: true,
        },
        {
            id: "client_order_id",
            accessorKey: "client_order_id",
            header: "Client Order ID",
            size: 180,
            minSize: 150,
            enableTooltip: true,
        },
        {
            id: "exchange_order_id",
            accessorKey: "exchange_order_id",
            header: "Exchange Order ID",
            size: 180,
            minSize: 150,
            enableTooltip: true,
        },
        {
            id: "our_id",
            accessorKey: "our_id",
            header: "Our ID",
            size: 150,
            minSize: 120,
            enableTooltip: true,
        },
        {
            id: "instrument_option_type",
            accessorKey: "instrument_option_type",
            header: "Option Type",
            size: 120,
            minSize: 100,
        },
        {
            id: "instrument_strike_price",
            accessorKey: "instrument_strike_price",
            header: "Strike Price",
            size: 120,
            minSize: 100,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value || "";
            },
        },
        {
            id: "instrument_expiration_date",
            accessorKey: "instrument_expiration_date",
            header: "Expiration Date",
            size: 140,
            minSize: 120,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value || "";
            },
        },
        {
            id: "instrument_currency_code",
            accessorKey: "instrument_currency_code",
            header: "Currency",
            size: 100,
            minSize: 80,
        },
        {
            id: "trigger_price",
            accessorKey: "trigger_price",
            header: "Trigger Price",
            size: 120,
            minSize: 100,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value || "";
            },
        },
        {
            id: "stop_price",
            accessorKey: "stop_price",
            header: "Stop Price",
            size: 120,
            minSize: 100,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value || "";
            },
        },
        {
            id: "cancelled_quantity",
            accessorKey: "cancelled_quantity",
            header: "Cancelled Qty",
            size: 120,
            minSize: 100,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value || "";
            },
        },
        {
            id: "is_rejected",
            accessorKey: "is_rejected",
            header: "Rejected",
            size: 100,
            minSize: 80,
            cell: (info) => {
                const value = info.getValue() as boolean;
                return value ? "Yes" : "No";
            },
        },
        {
            id: "reject_message",
            accessorKey: "reject_message",
            header: "Reject Message",
            size: 200,
            minSize: 150,
            enableTooltip: true,
        },
        {
            id: "message",
            accessorKey: "message",
            header: "Message",
            size: 200,
            minSize: 150,
            enableTooltip: true,
        },
        {
            id: "exchange_event_time",
            accessorKey: "exchange_event_time",
            header: "Exchange Event Time",
            size: 180,
            minSize: 150,
            cell: (info) => {
                const value = info.getValue() as string | null;
                return value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "";
            },
        },
        {
            id: "is_parent",
            accessorKey: "is_parent",
            header: "Is Parent",
            size: 100,
            minSize: 80,
            cell: (info) => {
                const value = info.getValue() as boolean;
                return value ? "Yes" : "No";
            },
        },
        {
            id: "is_child",
            accessorKey: "is_child",
            header: "Is Child",
            size: 100,
            minSize: 80,
            cell: (info) => {
                const value = info.getValue() as boolean;
                return value ? "Yes" : "No";
            },
        },
        {
            id: "parent_order_id",
            accessorKey: "parent_order_id",
            header: "Parent Order ID",
            size: 150,
            minSize: 120,
            enableTooltip: true,
        },
        {
            id: "leg_count",
            accessorKey: "leg_count",
            header: "Leg Count",
            size: 100,
            minSize: 80,
        },
    ], []);

    // Default visible columns - important ones for futures and options
    const defaultColumnVisibility = useMemo(() => ({
        event_type: true,
        event_time: true,
        instrument_name: true,
        instrument_product: false,
        buy_sell_type: true,
        order_quantity: true,
        filled_quantity: true,
        limit_price: true,
        avg_fill_price: true,
        order_status: true,
        trading_account_name: true,
        client_order_id: false,
        exchange_order_id: false,
        our_id: false,
        instrument_option_type: false,
        instrument_strike_price: false,
        instrument_expiration_date: false,
        instrument_currency_code: false,
        trigger_price: false,
        stop_price: false,
        cancelled_quantity: false,
        working_quantity: false,
        order_type: true,
        time_in_force: false,
        clearing_account_name: false,
        is_rejected: false,
        reject_message: true,
        message: true,
        exchange_event_time: false,
        is_parent: false,
        is_child: false,
        parent_order_id: false,
        leg_count: true,
    }), []);

    return (
        <div className="flex flex-col gap-4">
            <h2 className="text-lg font-semibold">TT Orders</h2>

            {/* Filters */}
            <div className="flex flex-col gap-4 p-4 border rounded-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="start-date">Start Date</Label>
                        <input
                            id="start-date"
                            type="date"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="end-date">End Date</Label>
                        <input
                            id="end-date"
                            type="date"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <Label>Order Event Type</Label>
                    <div className="flex flex-wrap gap-2">
                        {allEventTypes.map((eventType) => {
                            const isSelected = selectedEventTypes.includes(eventType);
                            return (
                                <Badge
                                    key={eventType}
                                    variant={isSelected ? "default" : "outline"}
                                    className={cn(
                                        "cursor-pointer transition-colors",
                                        isSelected && "bg-primary text-primary-foreground"
                                    )}
                                    onClick={() => toggleEventType(eventType)}
                                >
                                    {eventType}
                                </Badge>
                            );
                        })}
                    </div>
                </div>
                {!shouldEnableQuery && (
                    <p className="text-sm text-muted-foreground">
                        Please provide both start date and end date to load orders.
                    </p>
                )}
            </div>

            <div className="flex flex-col">
                {/* Results */}
                {!shouldEnableQuery ? (
                    <div className="p-4 border rounded-md">
                        <p className="text-muted-foreground">Select a date range and events to view orders</p>
                    </div>
                ) : ttOrdersResult.isLoading ? (
                    <div className="p-4 border rounded-md">
                        <p>Loading TT orders...</p>
                    </div>
                ) : ttOrdersResult.isError ? (
                    <div className="p-4 border rounded-md">
                        <p>Error loading TT orders: {(ttOrdersResult.error as any)?.message}</p>
                    </div>
                ) : (
                    <DataTable
                        data={events}
                        columns={columns}
                        enableSorting
                        enableMultiSort
                        enableFiltering
                        enableColumnVisibility
                        enableColumnPinning
                        enableExport
                        defaultColumnVisibility={defaultColumnVisibility}
                        emptyMessage="No TT orders found"
                        stickyHeaders
                        tableClassName="max-h-[calc(100vh-400px)] overflow-y-scroll"
                    />
                )}
            </div>
        </div>
    );
}
